/**
 * Sport IQ — Glossary Browser (basketball / football / soccer / track).
 * Search + category filter + grid of clickable term cards.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, ArrowLeft, X } from 'lucide-react';
import api from '../services/api';
import { getSportMeta } from '../lib/sportMeta';

const CATEGORY_ACCENT = {
  // Basketball
  'offense-action': '#F97316',
  'offense-set': '#EAB308',
  'defense-coverage': '#06B6D4',
  'defense-zone': '#3B82F6',
  'dribble-move': '#EC4899',
  'decision': '#A855F7',
  'finishing': '#22C55E',
  'transition': '#F43F5E',
  'iq-principle': '#D4AF37',
  'rebounding': '#F59E0B',
  // Football-specific
  'offense-concept': '#F97316',
  'offense-formation': '#EAB308',
  'passing-concept': '#3B82F6',
  'run-concept': '#EF4444',
  'defense-front': '#06B6D4',
  'defense-pressure': '#EC4899',
  // Soccer-specific
  'defense-action': '#06B6D4',
  'press': '#EC4899',
  // Track-specific
  'sprints': '#F97316',
  'distance': '#3B82F6',
  'field': '#22C55E',
  'relays': '#EC4899',
  'race-iq': '#D4AF37',
};

const BasketballIQGlossary = ({ sport = 'basketball' }) => {
  const meta = getSportMeta(sport);
  const ACCENT = meta.accent;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [playerType, setPlayerType] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (category) params.append('category', category);
    if (playerType) params.append('player_type', playerType);
    api.get(`/academy/${sport}-iq/glossary${params.toString() ? `?${params}` : ''}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sport, q, category, playerType]);

  const cats = useMemo(() => data?.categories || [], [data]);
  const playerTypes = useMemo(() => data?.player_types || [], [data]);

  return (
    <div className="min-h-screen bg-[#050508] text-white pt-16 pb-24" data-testid={`${sport}-iq-glossary`}>
      {/* Header */}
      <div className="border-b-2 border-zinc-900 py-10 px-6">
        <div className="container mx-auto max-w-6xl">
          <Link to={`/${sport}-iq`} className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-3" data-testid="back-to-hub">
            <ArrowLeft className="w-3 h-3" /> {meta.hub_title}
          </Link>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight">
            <span style={{ color: ACCENT }}>Glossary</span> Library
          </h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
            Click any term for the full breakdown: definition, when to use, when NOT to use,
            counters, related actions, and example reads.
          </p>

          {/* Search */}
          <div className="mt-6 max-w-xl relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search terms…"
              className="w-full bg-black border-2 border-zinc-800 pl-10 pr-10 py-3 text-sm outline-none"
              style={{ borderColor: q ? ACCENT : undefined }}
              data-testid="glossary-search"
            />
            {q && (
              <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white" data-testid="glossary-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Player Type chips — filter by role (basketball only) */}
          {sport === 'basketball' && playerTypes.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5 items-center" data-testid="player-type-row">
              <div className="text-[9px] tracking-[0.3em] uppercase font-black text-zinc-600 mr-1">For</div>
              {playerTypes.map((pt) => (
                <button
                  key={pt.key}
                  onClick={() => setPlayerType(pt.key === playerType || pt.key === 'any' ? '' : pt.key)}
                  className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border-2 transition ${
                    (playerType === pt.key || (!playerType && pt.key === 'any'))
                      ? 'text-black'
                      : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'
                  }`}
                  style={
                    playerType === pt.key || (!playerType && pt.key === 'any')
                      ? { backgroundColor: ACCENT, borderColor: ACCENT }
                      : {}
                  }
                  data-testid={`ptype-${pt.key}`}
                >
                  {pt.label} ({pt.count})
                </button>
              ))}
            </div>
          )}

          {/* Category chips */}
          <div className="flex flex-wrap gap-2 mt-5">
            <button
              onClick={() => setCategory('')}
              className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border-2 ${!category ? 'bg-white text-black border-white' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'}`}
              data-testid="cat-all"
            >
              All
            </button>
            {cats.filter((c) => c.count > 0).map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key === category ? '' : c.key)}
                className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border-2 transition ${
                  category === c.key ? 'text-black' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'
                }`}
                style={category === c.key ? { backgroundColor: CATEGORY_ACCENT[c.key] || ACCENT, borderColor: CATEGORY_ACCENT[c.key] || ACCENT } : {}}
                data-testid={`cat-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Terms grid */}
      <div className="container mx-auto max-w-6xl px-6 py-10">
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} /></div>
        ) : data?.terms?.length === 0 ? (
          <div className="py-20 text-center text-zinc-500 text-sm">No terms match — try a different search.</div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {data?.terms?.map((t) => {
              const accent = CATEGORY_ACCENT[t.category] || ACCENT;
              return (
                <Link
                  key={t.slug}
                  to={`/${sport}-iq/term/${t.slug}`}
                  className="group border-2 border-zinc-800 bg-zinc-950 p-5 hover:border-orange-500 transition-all"
                  data-testid={`term-card-${t.slug}`}
                  style={{ borderTopColor: accent, borderTopWidth: 3 }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[9px] tracking-[0.3em] uppercase font-black" style={{ color: accent }}>
                      {t.category?.replace(/-/g, ' ')}
                    </div>
                    {t.difficulty && t.difficulty !== 'foundational' && (
                      <div className="text-[8px] tracking-[0.3em] uppercase font-black text-zinc-600">{t.difficulty}</div>
                    )}
                  </div>
                  <h3 className="text-white font-black text-base leading-tight">{t.term}</h3>
                  <p className="text-[12px] text-zinc-400 mt-2 leading-relaxed">{t.one_line}</p>
                  {t.related_terms?.length > 0 && (
                    <div className="mt-3 text-[10px] text-zinc-600">
                      Related: <span className="text-zinc-400">{t.related_terms.slice(0, 3).join(' · ').replace(/-/g, ' ')}</span>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BasketballIQGlossary;
